/* EU image localisation. Select a translation before activating deferred images. */
(() => {
  'use strict';
  const defaults = {fr:'fr',nl:'nl',be:'nl',de:'de',it:'it',es:'es',pl:'pl',pt:'pt',se:'sv',dk:'da',no:'no',hu:'hu',ro:'ro',cz:'cs',at:'de',ch:'de',lt:'lt',lv:'lv',ee:'et',gr:'el',ie:'en',co:'en'};
  const known = new Set('en fr nl de it es pl pt sv da hu ro cs lt lv et el hr fi no sk sl tr'.split(' '));
  const pathLanguage = (location.pathname.split('/')[1] || '').toLowerCase().split('-')[0];
  const documentLanguage = (document.documentElement.lang || '').toLowerCase().split('-')[0];
  const language = known.has(pathLanguage) ? pathLanguage : known.has(documentLanguage) ? documentLanguage : defaults[location.hostname.split('.').pop()] || 'en';
  if (language === 'en') return;
  const suffix = '-' + language;
  const pending = new Map();
  const storageKey = 'shopsta-image-locales-v2';
  let cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch { /* Storage can be disabled. */ }
  if (!cache || typeof cache !== 'object' || Array.isArray(cache)) cache = {};
  function parse(source) {
    if (!source || /^(data|blob):/i.test(source)) return null;
    let url;
    try { url = new URL(source, location.href); } catch { return null; }
    if (url.hostname !== 'cdn.shopify.com' && !url.pathname.startsWith('/cdn/shop/files/')) return null;
    const match = url.pathname.match(/^(.*?)(_(?:\d+x\d*|x\d+)(?:_crop_(?:top|center|bottom|left|right))?)?\.(jpe?g|png|webp|gif)$/i);
    if (!match || match[1].toLowerCase().endsWith(suffix)) return null;
    return {url, base:match[1].replace(/-en$/i,''), size:match[2] || '', ext:match[3].toLowerCase()};
  }
  function build(parsed, extension, sized = true) {
    const url = new URL(parsed.url);
    url.pathname = parsed.base + suffix + (sized ? parsed.size : '') + '.' + extension;
    if (!sized) { url.searchParams.delete('width'); url.searchParams.delete('height'); }
    return url.href;
  }
  async function resolve(parsed) {
    const key = parsed.url.origin + parsed.base + suffix + '|' + (parsed.url.searchParams.get('v') || '');
    const entry = cache[key];
    if (entry && entry.until > Date.now() && (entry.ext === null || ['jpg','jpeg','png','webp','gif'].includes(entry.ext))) return entry.ext;
    if (pending.has(key)) return pending.get(key);
    const task = (async () => {
      let transient = false;
      for (const extension of new Set([parsed.ext, 'jpg', 'jpeg', 'png', 'webp'])) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 1800);
        try {
          const response = await fetch(build(parsed, extension, false), {method:'HEAD', signal:controller.signal});
          if (response.ok) { remember(key, extension); return extension; }
          if (response.status !== 404) transient = true;
        } catch { transient = true; }
        finally { clearTimeout(timer); }
      }
      if (!transient) remember(key, null);
      return null;
    })();
    pending.set(key, task);
    try { return await task; } finally { pending.delete(key); }
  }
  function remember(key, ext) {
    cache[key] = {ext, until:Date.now() + (ext ? 86400000 : 600000)};
    const keys = Object.keys(cache);
    if (keys.length > 250) for (const old of keys.slice(0, keys.length - 250)) delete cache[old];
    try { sessionStorage.setItem(storageKey, JSON.stringify(cache)); } catch { /* Memory cache still works. */ }
  }
  window.ShopstaLocalizedImage = async source => {
    const parsed = parse(source);
    if (!parsed) return source;
    const extension = await resolve(parsed);
    return extension ? build(parsed, extension) : source;
  };
  function rewrite(source, extension) {
    const parsed = parse(source);
    return parsed ? build(parsed, extension) : source;
  }
  async function localizeImage(image) {
    const sourceAttribute = image.hasAttribute('data-gallery-thumbnail-src') ? 'data-gallery-thumbnail-src' : image.hasAttribute('data-gallery-src') ? 'data-gallery-src' : 'src';
    const parsed = parse(image.getAttribute(sourceAttribute));
    if (!parsed) return;
    const extension = await resolve(parsed);
    if (!extension || !image.isConnected) return;
    const srcsetAttribute = image.hasAttribute('data-gallery-thumbnail-srcset') ? 'data-gallery-thumbnail-srcset' : image.hasAttribute('data-gallery-srcset') ? 'data-gallery-srcset' : 'srcset';
    const srcset = image.getAttribute(srcsetAttribute);
    if (srcset && !srcset.startsWith('data:')) {
      image.setAttribute(srcsetAttribute, srcset.replace(/[^\s,]+/g, token => /^\d+(?:\.\d+)?[wx]$/.test(token) ? token : rewrite(token, extension)));
    }
    image.setAttribute(sourceAttribute, build(parsed, extension));
  }
  window.ShopstaLocalizeGalleryFigure = (figure, complete = () => {}) => {
    if (!figure || figure.getAttribute('data-gallery-index') === '0') { complete(); return; }
    if (!figure.shopstaLocaleTask) {
      figure.shopstaLocaleTask = (async () => {
        const image = figure.querySelector('[data-gallery-image]');
        if (image) await localizeImage(image);
        const zoom = figure.getAttribute('data-zoom');
        if (zoom) figure.setAttribute('data-zoom', await window.ShopstaLocalizedImage(zoom));
        const background = figure.querySelector('.product-gallery--image-background');
        const deferred = background?.getAttribute('data-gallery-background');
        if (deferred) background.setAttribute('data-gallery-background', await window.ShopstaLocalizedImage(deferred));
      })().catch(() => {});
    }
    figure.shopstaLocaleTask.then(complete, complete);
  };
  // The gallery owns visibility and activation; resolve the locale first so a
  // hidden thumbnail never downloads both its original and translated image.
  window.ShopstaLocalizeGalleryThumbnail = (image, complete = () => {}) => {
    if (image.closest('[data-gallery-thumbnail]')?.getAttribute('data-gallery-index') === '0') { complete(); return; }
    localizeImage(image).then(complete, complete);
  };
})();
